import Web3 from 'web3'
import { getEthereum, getWeb3 } from './runtime-globals'
import { buildAddEthereumChainParams, normalizeWalletChainId, parseRpcUrls } from './wallet-config'

const DEFAULT_CHAIN_NAME = 'Treasurenet'
const DEFAULT_CURRENCY_NAME = 'UNIT'
const DEFAULT_CURRENCY_SYMBOL = 'UNIT'
const DEFAULT_READONLY_RPC_URL = 'https://rpc.treasurenet.io'

export function getWalletWeb3() {
  return getWeb3()
}

export function getConfiguredRpcUrls() {
  const value = process.env.VUE_APP_RPC_URLS
  if (!value) {
    return [DEFAULT_READONLY_RPC_URL]
  }

  try {
    return parseRpcUrls(value)
  } catch (error) {
    return [DEFAULT_READONLY_RPC_URL]
  }
}

export function getReadOnlyWeb3() {
  return new Web3(getConfiguredRpcUrls()[0])
}

export async function getPreferredWeb3() {
  const walletWeb3 = getWalletWeb3()
  if (!walletWeb3) {
    return getReadOnlyWeb3()
  }

  try {
    const accounts = await walletWeb3.eth.getAccounts()
    if (accounts && accounts.length > 0) {
      return walletWeb3
    }
  } catch (error) {
    return getReadOnlyWeb3()
  }

  return getReadOnlyWeb3()
}

export async function requestWalletAccounts() {
  const ethereum = getEthereum()
  if (!ethereum) {
    throw new Error('MetaMask is not available')
  }

  await ethereum.request({ method: 'eth_requestAccounts' })
  return ethereum.request({ method: 'eth_accounts' })
}

export async function connectWallet() {
  const accounts = await requestWalletAccounts()
  if (!accounts[0]) {
    throw new Error('Not able to get accounts')
  }

  return accounts[0]
}

export async function getCurrentChainId() {
  const ethereum = getEthereum()
  if (!ethereum) {
    throw new Error('MetaMask is not available')
  }

  return ethereum.request({ method: 'eth_chainId' })
}

export async function ensureWalletChain() {
  const ethereum = getEthereum()
  if (!ethereum) {
    throw new Error('MetaMask is not available')
  }

  const chainId = normalizeWalletChainId(process.env.VUE_APP_CHAINID)
  try {
    await ethereum.request({
      method: 'wallet_switchEthereumChain',
      params: [{ chainId }]
    })
  } catch (error) {
    if (error.code !== 4902) {
      throw error
    }

    await ethereum.request({
      method: 'wallet_addEthereumChain',
      params: buildAddEthereumChainParams({
        chainId,
        rpcUrls: process.env.VUE_APP_RPC_URLS,
        chainName: DEFAULT_CHAIN_NAME,
        currencyName: DEFAULT_CURRENCY_NAME,
        currencySymbol: DEFAULT_CURRENCY_SYMBOL
      })
    })
  }

  return chainId
}

export function ensureWalletReady({ web3, contract }) {
  return Boolean(web3 && contract)
}

export function createTxPayload({ contract, method, args = [], from, gas, value = 0, contractAddress }) {
  if (!contract || !contract.methods || typeof contract.methods[method] !== 'function') {
    throw new Error(`Contract method is not available: ${method}`)
  }

  const txBuilder = contract.methods[method](...args)
  return {
    txBuilder,
    payload: {
      gas,
      from,
      to: contractAddress,
      value,
      data: txBuilder.encodeABI()
    }
  }
}
